import React from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getAwardTheme } from "../goldenAwardTheme";

import "./goldenAwardQuestionCard.css";

const buildOptions = (answerOptions) =>
  answerOptions.map((option) => ({ value: option, label: option }));

const GoldenAwardQuestionCard = ({ question, initialAnswer, submitLabel, onAnswer }) => {
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.webKey;

  const options = React.useMemo(
    () => buildOptions(question.answer_options),
    [question.answer_options],
  );

  const initialOption = React.useMemo(() => {
    if (!initialAnswer) return null;
    return options.find((option) => option.value === initialAnswer) || null;
  }, [initialAnswer, options]);

  const [selectedOption, setSelectedOption] = React.useState(initialOption);
  // idle -> submitting (awaiting the API call) -> submitted (brief success
  // state, held for a beat before the parent advances/closes the modal).
  //
  // This card is never unmounted between answers — the Dialog uses
  // `keepMounted` and SwipeableViews renders every question's slide up
  // front — so `status` MUST be reset back to "idle" locally after the
  // success beat, otherwise the button stays stuck on "Submitted" forever
  // and re-opening the question for editing has no working button.
  const [status, setStatus] = React.useState("idle");

  const theme = getAwardTheme(question.question_text);

  const hasChanged = !!selectedOption && selectedOption.value !== initialAnswer;

  const handleSubmit = async () => {
    if (!isLoggedIn) {
      navigate("/mobile-login");
      return;
    }
    if (!hasChanged || status !== "idle") return;
    setStatus("submitting");
    const ok = await onAnswer(selectedOption.value);
    if (ok) {
      setStatus("submitted");
      setTimeout(() => setStatus("idle"), 700);
    } else {
      setStatus("idle");
    }
  };

  return (
    <div className="goldenAwardQuestionCard">
      <div className="goldenAwardQuestionCard-badge">
        {theme.image ? (
          theme.tinted ? (
            <span
              className="goldenAwardQuestionCard-badgeImg goldenAwardQuestionCard-badgeImg-tinted"
              style={{ WebkitMaskImage: `url(${theme.image})`, maskImage: `url(${theme.image})` }}
            />
          ) : (
            <img src={theme.image} alt="" className="goldenAwardQuestionCard-badgeImg" />
          )
        ) : (
          <theme.Icon fontSize="inherit" />
        )}
      </div>
      <p className="goldenAwardQuestionCard-text">{localStorage.language === "en" ? question.question_text : question.question_text_malayalam}</p>
      {isLoggedIn &&
      <Select
        options={options}
        value={selectedOption}
        onChange={(option) => setSelectedOption(option)}
        placeholder="Select an answer"
        className="goldenAwardQuestionCard-select"
        isDisabled={status !== "idle"}
        menuPlacement="auto"
        menuPosition="fixed"
        // Portaled to <body> because this card renders inside a
        // SwipeableViews slide, which applies a CSS transform — a
        // position:fixed menu would otherwise be mis-positioned relative
        // to that transformed ancestor instead of the viewport.
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (provided) => ({ ...provided, zIndex: 1400 }),
          menu: (provided) => ({ ...provided, width: "100%" }),
          menuList: (provided) => ({ ...provided, maxHeight: "220px", overflowY: "auto" }),
        }}
      />}
      <Button
        type="button"
        disabled={(isLoggedIn && !hasChanged) || status !== "idle"}
        className={
          status === "submitted"
            ? "goldenAwardQuestionCard-submitBtn goldenAwardQuestionCard-submitBtn-submitted"
            : !isLoggedIn || hasChanged
              ? "goldenAwardQuestionCard-submitBtn"
              : "goldenAwardQuestionCard-submitBtn-disabled"
        }
        onClick={handleSubmit}
      >
        {status === "submitting" ? (
          <CircularProgress size={18} thickness={5} className="goldenAwardQuestionCard-spinner" />
        ) : status === "submitted" ? (
          <span className="goldenAwardQuestionCard-submitted">
            <CheckCircleIcon fontSize="inherit" /> Submitted
          </span>
        ) : isLoggedIn ? (
          submitLabel
        ) : (
          "LOGIN"
        )}
      </Button>
    </div>
  );
};

export default GoldenAwardQuestionCard;
